/* Клиент API бота (docs/03-api.md §1): база …/bot/v1, JSON, Bearer-токен сессии, ошибки { error: { code, message, field, details } }.
   Токен живёт только в памяти страницы (§1.2, не в localStorage). 401 session_expired — новый POST /app/session той же initData и повтор запроса.
   429 / 503 / 500 и обрыв сети — повтор после Retry-After или 2, 4, 8 с; POST повторяется с тем же Idempotency-Key (§1.7, §1.8). */
import type { ApiErrorBody, SessionResponse } from './types';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public field: string | null = null,
    public details: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ClientConfig {
  base: string;
  initData(): string;
  /** Где открыт Mini App: backend выбирает бота этой платформы для проверки initData (docs/65 §3) */
  platform?(): string;
  lang(): string;
  /** Пауза перед повтором: в тестах подменяется на мгновенную */
  sleep?(ms: number): Promise<void>;
  /** Чего ждать перед первым запросом (запуск моков) */
  ready?(): Promise<unknown>;
}

const RETRY_STATUS = new Set([429, 500, 503]);
const BACKOFF = [2000, 4000, 8000];

/** Повтор безопасен для чтения, PUT, DELETE, записи по версии и POST с ключом идемпотентности.
    POST без ключа мог уже выполниться: второй раз его не шлём, ошибка уходит сразу */
function replayable(o: RequestOptions) {
  const method = o.method || 'GET';
  return method === 'GET' || method === 'PUT' || method === 'DELETE' || !!o.ifMatch || typeof o.idempotency === 'string';
}

export const uuid = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 3) | 8).toString(16);
      });

export interface RequestOptions {
  /** Квитанция даёт право только на чтение статуса удаления, когда обычная сессия уже отозвана. */
  erasureReceipt?: string;
  mergeReceipt?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Ключ идемпотентности: один на намерение пользователя; true — создать новый */
  idempotency?: string | true;
  /** Условная запись ресурса; сервер отклоняет устаревшую версию. */
  ifMatch?: string;
  signal?: AbortSignal;
}

export function createClient(cfg: ClientConfig) {
  let token: string | null = null;
  let session: Promise<SessionResponse> | null = null;
  /** Поколение сессии: растёт на reset() и при смене владельца после входа. Кэши авторизованных файлов
      привязываются к нему и не отдают картинки прежнего владельца. */
  let epoch = 0;
  let owner: number | null = null;
  const sleep = cfg.sleep || ((ms: number) => new Promise<void>(r => setTimeout(r, ms)));

  async function parse(res: Response): Promise<never> {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      /* тело не JSON — код по статусу */
    }
    const e = body?.error;
    throw new ApiError(res.status, e?.code || 'internal_error', e?.message || res.statusText, e?.field ?? null, e?.details ?? null);
  }

  async function raw(path: string, o: RequestOptions, auth: boolean): Promise<Response> {
    await cfg.ready?.();
    const url = new URL(cfg.base.replace(/\/$/, '') + path, location.href);
    for (const [k, v] of Object.entries(o.query || {})) if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    /* содержимое студии (главная, каталог, приложения, подборки, экраны) Backend отдаёт на языке из ?locale (по умолчанию ru) */
    if ((o.method || 'GET') === 'GET' && path.startsWith('/app/studio/') && !path.includes('/improve') && !url.searchParams.has('locale'))
      url.searchParams.set('locale', cfg.lang());
    const h: Record<string, string> = { Accept: 'application/json', 'Accept-Language': cfg.lang() };
    if (o.body !== undefined) h['Content-Type'] = 'application/json';
    if (auth && token) h.Authorization = 'Bearer ' + token;
    if (typeof o.idempotency === 'string') h['Idempotency-Key'] = o.idempotency;
    if (o.ifMatch) h['If-Match'] = o.ifMatch;
    if (o.erasureReceipt) h['X-Erasure-Receipt'] = o.erasureReceipt;
    if (o.mergeReceipt) h['X-Merge-Receipt'] = o.mergeReceipt;
    return fetch(url, {
      method: o.method || 'GET',
      headers: h,
      body: o.body === undefined ? undefined : JSON.stringify(o.body),
      signal: o.signal,
    });
  }

  /** POST /app/session (§3.1): один на страницу, общий для одновременных запросов */
  function login(force = false): Promise<SessionResponse> {
    if (session && !force) return session;
    const p = (async () => {
      const kind = cfg.platform?.();
      const body = kind === 'max' ? { init_data: cfg.initData(), platform: 'max' } : { init_data: cfg.initData() };
      const res = await raw('/app/session', { method: 'POST', body }, false);
      if (!res.ok) return parse(res);
      const s = (await res.json()) as SessionResponse;
      token = s.session_token;
      const id = s.me?.user?.id ?? null;
      if (owner !== null && id !== owner) epoch++;
      owner = id;
      return s;
    })();
    session = p;
    p.catch(() => {
      if (session === p) session = null;
    });
    return p;
  }

  async function request<T>(path: string, o: RequestOptions = {}): Promise<T> {
    const opts = { ...o, idempotency: o.idempotency === true ? uuid() : o.idempotency };
    if (!token && !opts.erasureReceipt && !opts.mergeReceipt) await login();
    const retries = replayable(opts) ? BACKOFF.length : 0;
    let renewed = false;
    for (let attempt = 0; ; attempt++) {
      let res: Response;
      try {
        res = await raw(path, opts, !opts.erasureReceipt && !opts.mergeReceipt);
      } catch (e) {
        if (opts.signal?.aborted || attempt >= retries) throw e;
        await sleep(BACKOFF[attempt]);
        continue;
      }
      if (res.ok) return (res.status === 204 ? undefined : await res.json()) as T;
      if (res.status === 401 && !renewed && !opts.erasureReceipt && !opts.mergeReceipt) {
        const err = await parse(res).catch((x: ApiError) => x);
        if (err.code !== 'session_expired' && err.code !== 'unauthorized') throw err;
        renewed = true;
        await login(true);
        attempt--;
        continue;
      }
      if (RETRY_STATUS.has(res.status) && attempt < retries) {
        const ra = Number(res.headers.get('Retry-After'));
        await sleep(ra > 0 ? ra * 1000 : BACKOFF[attempt]);
        continue;
      }
      return parse(res);
    }
  }

  async function download(path: string, signal?: AbortSignal): Promise<Blob> {
    if (!token) await login();
    let renewed = false;
    for (let attempt = 0; ; attempt++) {
      let res: Response;
      try {
        res = await raw(path, { signal }, true);
      } catch (error) {
        if (signal?.aborted || attempt >= BACKOFF.length) throw error;
        await sleep(BACKOFF[attempt]);
        continue;
      }
      if (res.ok) return res.blob();
      if (res.status === 401 && !renewed) {
        const error = await parse(res).catch((value: ApiError) => value);
        if (error.code !== 'session_expired' && error.code !== 'unauthorized') throw error;
        renewed = true;
        await login(true);
        attempt--;
        continue;
      }
      if (RETRY_STATUS.has(res.status) && attempt < BACKOFF.length) {
        await sleep(BACKOFF[attempt]);
        continue;
      }
      return parse(res);
    }
  }

  return {
    login,
    request,
    download,
    /** Относительный PUT URL Backend разрешается от API host, абсолютный S3 URL остаётся прежним. */
    resolveUploadUrl(url: string) {
      return new URL(url, new URL(cfg.base, location.href)).href;
    },
    /** Токен выдаётся только для PUT в собственный upload API, но не для внешнего S3 URL. */
    uploadTokenFor(url: string) {
      const base = new URL(cfg.base.replace(/\/$/, ''), location.href);
      const target = new URL(url, base);
      const prefix = `${base.pathname.replace(/\/$/, '')}/app/uploads/`;
      if (target.origin !== base.origin ||
          !new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[0-9a-fA-F-]{36}/content$`).test(target.pathname))
        return null;
      return token;
    },
    get token() {
      return token;
    },
    get epoch() {
      return epoch;
    },
    reset() {
      token = null;
      session = null;
      owner = null;
      epoch++;
    },
  };
}

export type ApiClient = ReturnType<typeof createClient>;
